import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { KEY_CODES } from 'constants/keyboard';
import {
  CLOSED_CURLY_BRACKET,
  OPEN_BRACKET_WITH_NO_PAIR,
  OPEN_CURLY_BRACKET,
} from 'constants/textFieldBuilder';
import { checkIsBracketsOpen } from 'utils/checkIsBracketsOpen';
import {
  caseInsensitiveIncludes,
  generateGroupWithNameValue,
  getValuesInsideCurlyBrackets,
} from 'utils/string';

const TextFieldBuilder = ({
  value: formValue,
  error = false,
  dataTestIdOption = '',
  dataTestIdInput = '',
  helperText = '',
  label,
  name,
  required = false,
  handleChange,
  setInForm,
  validateField,
  disabled = false,
  className = '',
  dataTestId = '',
  'data-testid': dataTestIdAttr,
  options,
  groupBy,
  includeGroupName = false,
  rows = 1,
  maxRows = 5,
  containerClassName = '',
  isHidden = false,
}) => {
  const ref = useRef();
  const resolvedDataTestId = dataTestId || dataTestIdAttr || '';
  const cursorIndex = ref.current?.selectionEnd;
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const optionStartIndex = formValue.match(OPEN_BRACKET_WITH_NO_PAIR)?.index + 1;

  const optionFilterValue = useMemo(
    () => formValue?.slice(optionStartIndex, cursorIndex),
    [cursorIndex, formValue, optionStartIndex],
  );

  const openBracketIndex = cursorIndex - optionFilterValue?.length;
  const valueBeforeOption = formValue.slice(0, openBracketIndex);
  const valueAfterOption = formValue.slice(cursorIndex);

  const value = useMemo(() => ({ name: formValue, value: formValue }), [formValue]);

  const valuesInString = useMemo(
    () => getValuesInsideCurlyBrackets(formValue, includeGroupName),
    [formValue, includeGroupName],
  );

  const onAutocompleteChange = useCallback(
    (e, option) => {
      const messageVariableString = generateGroupWithNameValue(option?.group, option?.name);

      const optionWithBracket =
        (includeGroupName ? messageVariableString : option?.name) + CLOSED_CURLY_BRACKET;
      const updatedValue = valueBeforeOption + optionWithBracket + valueAfterOption;

      onAutocompleteChangeInForm({
        setInForm: fieldname => setInForm(fieldname, option ? updatedValue : ''),
        validateField: fieldname => validateField(fieldname, option ? updatedValue : ''),
      })(e, option);
      setIsOptionsOpen(false);
    },
    [includeGroupName, valueBeforeOption, valueAfterOption, setInForm, validateField],
  );

  const filteredOptions = useMemo(() => {
    return [...options].filter(
      ({ name, group }) =>
        caseInsensitiveIncludes(name, optionFilterValue) &&
        !valuesInString.some(
          value => value === (includeGroupName ? generateGroupWithNameValue(group, name) : name),
        ),
    );
  }, [includeGroupName, optionFilterValue, options, valuesInString]);

  const onInputChange = useCallback(
    e => {
      handleChange(e);
    },
    [handleChange],
  );

  const onCloseAutocomplete = useCallback(e => {
    if (e.keyCode === KEY_CODES.ESC) {
      setIsOptionsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!checkIsBracketsOpen(formValue)) {
      setIsOptionsOpen(false);

      return;
    }

    if (formValue.includes(OPEN_CURLY_BRACKET)) {
      setIsOptionsOpen(true);
    }
  }, [formValue, optionStartIndex]);

  const inputProps = useMemo(
    () => ({
      inputRef: ref,
      onChange: onInputChange,
      noValidate: false,
      multiline: true,
      maxRows,
      rows,
    }),
    [onInputChange, rows, maxRows],
  );

  return (
    <AutocompleteCDS
      freeSolo
      isLazyWrapped
      className={className}
      containerClassName={containerClassName}
      dataTestId={resolvedDataTestId}
      dataTestIdInput={dataTestIdInput}
      dataTestIdOption={dataTestIdOption}
      disabled={disabled}
      error={error}
      groupBy={groupBy}
      helperText={helperText}
      inputProps={inputProps}
      isHidden={isHidden}
      label={label}
      name={name}
      open={isOptionsOpen}
      options={filteredOptions}
      required={required}
      value={value}
      onChange={onAutocompleteChange}
      onClose={onCloseAutocomplete}
    />
  );
};

TextFieldBuilder.propTypes = {
  options: PropTypes.array.isRequired,
  groupBy: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  setInForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  className: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  dataTestId: PropTypes.string,
  'data-testid': PropTypes.string,
  dataTestIdOption: PropTypes.string,
  dataTestIdInput: PropTypes.string,
  required: PropTypes.bool,
  includeGroupName: PropTypes.bool,
  rows: PropTypes.number,
  maxRows: PropTypes.number,
  containerClassName: PropTypes.string,
  disabled: PropTypes.bool,
  isHidden: PropTypes.bool,
};

export default memo(TextFieldBuilder);
